/**
 * Smart Money: структура, ликвидность, зоны.
 *
 * Язык этой школы — снятие ликвидности, слом структуры, разрывы и
 * ордер-блоки. Каждое из этих слов — блок движка, и идеи здесь собраны
 * из тех же блоков, что доступны в конструкторе.
 */
import { above, adx, divergenceParams, happened, ind, inZone, mirror, number, premium, recently, rvol, spec, structureTrend, swingParams, TRAIL, weight, crossDown, crossUp, } from './kit.js';
export const SMART_MONEY_TEMPLATES = [
    {
        id: 'liquidity-sweep',
        name: 'Снятие ликвидности',
        icon: '💧',
        category: 'smart-money',
        popular: true,
        hint: 'Тень зашла за экстремум и закрытие вернулось: стопы собраны, движения дальше нет.',
        steps: ['Ликвидность снята', 'Цена в нужной половине диапазона', 'Объём'],
        keywords: ['ликвидность', 'sweep', 'стопы'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'За каждым заметным минимумом стоят стопы покупателей. Цена кольнула туда, собрала их и вернулась.',
            why: 'Крупному участнику нужны встречные заявки, чтобы набрать позицию, — он находит их в чужих стопах.',
            fails: 'Если после снятия цена всё-таки закрепляется за экстремумом, это не сбор стопов, а пробой.',
            tip: 'Снятие сильнее у «очевидных» уровней: двойные минимумы, круглые числа, дневные экстремумы.',
        },
        build: (o) => spec(o, 'Снятие ликвидности', mirror((side) => ({
            context: [],
            trigger: happened(ind('sweep', swingParams, side.liquidity)),
            confirmations: [weight('c1', inZone(side, 50), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 50),
    },
    {
        id: 'bos',
        name: 'Пробой структуры (BOS)',
        icon: '🔨',
        category: 'smart-money',
        hint: 'Цена ушла за прошлый экстремум по ходу тренда — движение продолжается.',
        steps: ['Тренд достаточно силён', 'Структура пробита в нашу сторону', 'Объём'],
        keywords: ['bos', 'структура', 'пробой'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Структура — это последовательность вершин и впадин. Пробой структуры по ходу тренда — новый максимум за прошлой вершиной.',
            why: 'Каждый такой пробой подтверждает, что тренд жив и сторона, которая его ведёт, всё ещё сильнее.',
            fails: 'Поздние пробои в конце тренда: вершина обновлена, но сил хватило только на это.',
            tip: 'Первый-второй BOS после слома характера — самые ценные; пятый-шестой — уже поздно.',
        },
        build: (o) => spec(o, 'Пробой структуры (BOS)', mirror((side) => ({
            context: [above(adx, number(18))],
            trigger: happened(ind('bos', swingParams, side.way)),
            confirmations: [weight('c1', above(adx, number(25)), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 0),
    },
    {
        id: 'structure',
        name: 'Слом структуры (MSS)',
        icon: '🔀',
        category: 'smart-money',
        popular: true,
        hint: 'Вход на сломе характера: рынок впервые пошёл против прежнего направления.',
        steps: ['Слом характера', 'Тренд набирает силу', 'Объём'],
        keywords: ['choch', 'mss', 'слом', 'смена характера'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'Рынок долго делал всё более низкие минимумы, а теперь впервые пробил последнюю вершину вверх — характер сменился.',
            why: 'Слом характера — первое доказательство, что прежняя сторона больше не контролирует рынок.',
            fails: 'Один слом — ещё не разворот: часто это лишь глубокий откат перед продолжением.',
            tip: 'Лучший вход — не на самом сломе, а на откате после него (см. «Смена характера в дискаунте»).',
        },
        build: (o) => spec(o, 'Слом структуры (MSS)', mirror((side) => ({
            context: [],
            trigger: happened(ind('choch', swingParams, side.way)),
            confirmations: [weight('c1', above(adx, number(20)), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 0),
    },
    {
        id: 'divergence-structural',
        name: 'Структурная дивергенция',
        icon: '🔄',
        category: 'smart-money',
        hint: 'Дивергенция у снятого экстремума с подтверждением смены структуры. Редкий и веский сигнал.',
        steps: ['Снят экстремум — забрана ликвидность', 'Цена и RSI разошлись', 'Структура сломана в нашу сторону'],
        keywords: ['дивергенция', 'структура', 'choch', 'слом', 'ликвидность'],
        passport: {
            level: 'pro',
            market: 'any',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Три признака разворота сходятся вместе: стопы за экстремумом собраны, импульс не подтвердил новый экстремум, структура сломана.',
            why: 'Каждый признак по отдельности часто ошибается; одновременно они сходятся только у настоящих поворотов.',
            fails: 'Редкость: неделями ни одного сигнала. И на одной монете может не встретиться совсем.',
            tip: 'Хороша на 4ч и дне; на 5 минутах такая связка — чаще шум.',
        },
        build: (o) => spec(o, 'Структурная дивергенция', mirror((side) => ({
            context: [recently(ind('sweep', swingParams, side.sign > 0 ? 'low' : 'high'), TRAIL)],
            trigger: happened(ind('divergence', divergenceParams, side.way)),
            confirmations: [weight('c1', recently(ind('choch', swingParams, side.way), TRAIL), 70), weight('c2', above(rvol, number(1.2)), 30)],
        })), 40),
    },
    {
        id: 'fvg-entry',
        name: 'Вход в разрыв (FVG)',
        icon: '🟦',
        category: 'smart-money',
        hint: 'Цена вернулась в промежуток, который проскочила на импульсе, — вход оттуда.',
        steps: ['Цена вернулась в разрыв', 'Структура смотрит в нашу сторону', 'Объём'],
        keywords: ['fvg', 'разрыв', 'имбаланс', 'gap'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'На сильном движении цена проскочила участок, почти не торгуясь, — остался «разрыв». Часто она возвращается его заполнить.',
            why: 'Разрыв — место, где не хватило встречных заявок. На возврате их добирают, и движение продолжается.',
            fails: 'Против структуры разрыв просто закрывается насквозь, и цена идёт дальше.',
            tip: 'Разрыв по направлению структуры — половина успеха. Без этого условия сигналов вдвое больше и хуже.',
        },
        build: (o) => spec(o, 'Вход в разрыв (FVG)', mirror((side) => ({
            context: [],
            trigger: happened(ind('fvg', { side: side.sign, size: 0.25 }, 'touched')),
            confirmations: [weight('c1', side.beyond(structureTrend(), number(0)), 60), weight('c2', above(rvol, number(1.2)), 40)],
        })), 50),
    },
    {
        id: 'smart-money',
        name: 'Ордер-блок',
        icon: '🧱',
        category: 'smart-money',
        popular: true,
        hint: 'Возврат в ордер-блок после пробоя структуры — вход туда, откуда начался импульс.',
        steps: ['Структура пробита', 'Цена вернулась в ордер-блок', 'Дискаунт и объём'],
        keywords: ['order block', 'ордер-блок', 'ob'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Ордер-блок — последняя свеча против движения перед сильным импульсом. Там крупный участник набирал позицию.',
            why: 'Когда цена возвращается туда, он «защищает» свой уровень — добирает ещё.',
            fails: 'Если после возврата структура ломается в обратную сторону — ордер-блок отработан и больше не держит.',
            tip: 'Лучшие ордер-блоки — в дискаунте (для покупок) и после снятия ликвидности.',
        },
        build: (o) => spec(o, 'Ордер-блок', mirror((side) => ({
            context: [recently(ind('bos', swingParams, side.way), 15)],
            trigger: happened(ind('order_block', { side: side.sign, strength: 1.5 }, 'touched')),
            confirmations: [weight('c1', inZone(side, 50), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 50),
    },
    {
        id: 'premium-discount',
        name: 'Премиум / дискаунт',
        icon: '⚖️',
        category: 'smart-money',
        hint: 'Покупать в нижней трети диапазона, продавать в верхней — по ходу структуры.',
        steps: ['Структура в нашу сторону', 'Цена вошла в нужную треть диапазона', 'Объём'],
        keywords: ['premium', 'discount', 'диапазон'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '15m',
            idea: 'Диапазон между последней вершиной и впадиной делится пополам: нижняя часть — «дёшево» (дискаунт), верхняя — «дорого» (премиум).',
            why: 'В растущем рынке выгоднее покупать со скидкой, чем догонять; крупные участники делают именно так.',
            fails: 'Когда структура разворачивается: «дёшево» становится ещё дешевле.',
            tip: 'Совмещайте с ордер-блоком или разрывом внутри дискаунта — это точное место входа.',
        },
        build: (o) => spec(o, 'Премиум / дискаунт', mirror((side) => ({
            context: [side.beyond(structureTrend(), number(0))],
            trigger: side.sign > 0 ? crossDown(premium(), number(30)) : crossUp(premium(), number(70)),
            confirmations: [weight('c1', above(rvol, number(1.1)), 100)],
        })), 0),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'sweep-fvg',
        name: 'Снятие + разрыв',
        icon: '💧',
        category: 'smart-money',
        hint: 'Сначала сняли ликвидность, затем на возврате цена пришла в оставленный разрыв — вход оттуда.',
        steps: ['Недавно снята ликвидность', 'Цена вернулась в разрыв', 'Дискаунт и объём'],
        keywords: ['sweep', 'fvg', 'ликвидность', 'разрыв', 'имбаланс'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '15m',
            idea: 'Цена собрала стопы за экстремумом, резко ушла обратно и оставила разрыв. Возврат в этот разрыв — точка входа.',
            why: 'Снятие показывает намерение, разрыв — силу, с которой развернули. Вместе — след крупного участника.',
            fails: 'Если разрыв закрывают насквозь, намерение не подтвердилось.',
            tip: 'Разрыв ближе к экстремуму надёжнее, чем разрыв посреди движения.',
        },
        build: (o) => spec(o, 'Снятие + разрыв', mirror((side) => ({
            context: [recently(ind('sweep', swingParams, side.liquidity), 15)],
            trigger: happened(ind('fvg', { side: side.sign, size: 0.25 }, 'touched')),
            confirmations: [weight('c1', inZone(side, 50), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 0),
    },
    {
        id: 'choch-discount',
        name: 'Смена характера в дискаунте',
        icon: '🔁',
        category: 'smart-money',
        hint: 'Характер рынка сменился, и цена откатилась в дешёвую треть диапазона — вход по новой стороне.',
        steps: ['Недавно был слом характера в нашу сторону', 'Цена откатилась в нужную треть', 'Объём'],
        keywords: ['choch', 'discount', 'дискаунт', 'премиум', 'смена характера'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'После слома характера не догонять цену, а дождаться отката в «дешёвую» часть нового диапазона.',
            why: 'Первый откат после смены стороны — место, где новые хозяева рынка добирают позицию.',
            fails: 'Если откат уходит глубже начала движения, слом оказался ложным.',
            tip: 'Защиту ставить за экстремум, с которого начался слом.',
        },
        build: (o) => spec(o, 'Смена характера в дискаунте', mirror((side) => ({
            context: [recently(ind('choch', swingParams, side.way), 30)],
            trigger: side.sign > 0 ? crossDown(premium(), number(35)) : crossUp(premium(), number(65)),
            confirmations: [weight('c1', above(rvol, number(1.1)), 50), weight('c2', side.beyond(structureTrend(), number(0)), 50)],
        })), 0),
    },
];
//# sourceMappingURL=smart-money.js.map